/**
 * @fileoverview Zod schemas and types for Bank Connections.
 * @module features/bank-connections/schemas/bankConnectionsSchema
 */

import { z } from 'zod'

/**
 * Bank connection status enum values.
 */
export const BankConnectionStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  ERROR: 'ERROR',
} as const

export type BankConnectionStatus = (typeof BankConnectionStatus)[keyof typeof BankConnectionStatus]

/**
 * Bank aggregator enum values.
 */
export const BankAggregator = {
  ENABLE_BANKING: 'ENABLE_BANKING',
} as const

export type BankAggregator = (typeof BankAggregator)[keyof typeof BankAggregator]

/**
 * Schema for available bank (ASPSP) information.
 */
export const bankDtoSchema = z.object({
  name: z.string(),
  country: z.string(),
  logo: z.string().nullable().optional(),
  bic: z.string().nullable().optional(),
  psuTypes: z.array(z.string()).optional().default([]),
})

export type BankDto = z.infer<typeof bankDtoSchema>

/**
 * Schema for bank connection details.
 */
export const bankConnectionDtoSchema = z.object({
  id: z.string().uuid(),
  aggregator: z.enum(['ENABLE_BANKING']),
  institutionName: z.string(),
  institutionCountry: z.string(),
  accountIban: z.string(),
  accountName: z.string().nullable().optional(),
  accountCurrency: z.string(),
  status: z.enum(['ACTIVE', 'EXPIRED', 'REVOKED', 'ERROR']),
  consentValidUntil: z.string().nullable().optional(),
  lastSyncedAt: z.string().nullable().optional(),
  createdAt: z.string(),
})

export type BankConnectionDto = z.infer<typeof bankConnectionDtoSchema>

/**
 * Schema for bank authorization start request.
 */
export const bankAuthStartRequestSchema = z.object({
  bankName: z.string().min(1, 'Bank name is required'),
  country: z.string().length(2, 'Country code must be 2 characters'),
})

export type BankAuthStartRequest = z.infer<typeof bankAuthStartRequestSchema>

/**
 * Schema for bank authorization start response.
 */
export const bankAuthStartResponseSchema = z.object({
  authorizationUrl: z.string().url(),
})

export type BankAuthStartResponse = z.infer<typeof bankAuthStartResponseSchema>

/**
 * Schema for sync result.
 */
export const syncResultDtoSchema = z.object({
  success: z.boolean().nullable().optional(),
  date: z.string().nullable().optional(),
  recordsProcessed: z.number().int().nullable().optional(),
  message: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  ordersProcessed: z.number().int().nullable().optional(),
  salesCreated: z.number().int().nullable().optional(),
  salesUpdated: z.number().int().nullable().optional(),
  salesSkipped: z.number().int().nullable().optional(),
})

export type SyncResultDto = z.infer<typeof syncResultDtoSchema>

/**
 * Schema for array of bank connections.
 */
export const bankConnectionsListSchema = z.array(bankConnectionDtoSchema)

/**
 * Schema for array of banks.
 */
export const banksListSchema = z.array(bankDtoSchema)
